import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown, false = anon, {} = authed
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // If we're processing a Google OAuth callback, skip /auth/me — AuthCallback
    // will POST /api/auth/google/session and call login() with the result.
    // ⚠ Only skip on the actual /auth/callback path. Stripe's success_url ALSO
    // includes `?session_id={CHECKOUT_SESSION_ID}` (both consulta and
    // suscripción), and using session_id alone would deadlock the app on
    // "Cargando…" for every user coming back from Stripe.
    const pathname = window.location.pathname || "";
    const isGoogleCallback =
      pathname === "/auth/callback" &&
      (window.location.hash.includes("session_id=") ||
        new URLSearchParams(window.location.search).get("session_id"));
    if (isGoogleCallback) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("saludia_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem("saludia_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = (token, userObj) => {
    localStorage.setItem("saludia_token", token);
    setUser(userObj);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("saludia_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
